import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

export const useAddItemsToWorkOrderChange = <T, P>({
    requestId,
    query,
    variables,
    onSuccessAddHandler,
}: {
    requestId: keyof Omit<T, '__typename'>;
    query: TypedDocumentNode<T, P>;
    variables: P;
    onSuccessAddHandler?: () => void;
}) => {
    const dispatch = useDispatch();

    const [isSubmitted, setIsSubmitted] = useState(false);

    const [addResponse, addStatus, addErr, addRequest] = useRequest({
        requestId: requestId as string,
        query,
        withEffect: false,
    });

    const [resetData] = useResetStoreData({
        requestId: requestId as string,
    });

    const addToWorkOrderChangeHandler = useCallback(() => {
        dispatch(addRequest(variables));

        setIsSubmitted(true);
    }, [addRequest, dispatch, variables]);

    useEffect(() => {
        // @ts-ignore
        if (isSubmitted && addStatus === RequestStatus.SUCCESS && !addResponse?.[requestId]?.message) {
            onSuccessAddHandler?.();
        }
    }, [addResponse, addStatus, isSubmitted, onSuccessAddHandler, requestId]);

    const closeErrorModal = useCallback(() => {
        setIsSubmitted(false);
        resetData(undefined);
    }, [resetData]);

    const handleCreateReport = useCallback(() => {
        // @ts-ignore
        ExportCSVReport(addResponse?.[requestId]?.validation);
    }, [addResponse, requestId]);

    return {
        addToWorkOrderChangeHandler,
        addResponse,
        addStatus,
        addErr,
        addIsSubmitted: isSubmitted,
        closeErrorModal,
        handleCreateReport,
    };
};
